import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "@redux/hooks";
import { RootState } from "@redux/store/store";
import { toggleMenu } from "@redux/slices/sideMenuSlice";
import { selectIsUserProfileLoading } from "@redux/slices/userProfileSlice";
import { useAuth0 } from "@auth0/auth0-react";

import { ReactComponent as HomeSvg } from "@assets/Icons/Icon.svg";

import {
  mainItems,
  managementItems,
  accountAreaItems,
  MenuItem,
  reflectorV2MenuItems,
  insightMenuItems,
  eventManagementItems,
} from "./MenuData";

import {
  expandMatchingParents,
  handleSelection,
} from "./MenuUtils";
import {
  adminSections,
  reflectorV1AdminSections,
  reflectorV2AdminSections,
  filterAdminSectionsByPermission,
} from "./components/AdminSections";
import { canAccessKontrolAlerts } from "@shared/navigation/helpers/canAccessKontrolAlerts";
import { ProductInternalName } from "./productConfig";
import {
  filterAccountAreaItems,
  filterKontrolEventManagementItems,
  filterSubscriptionsAdminSection,
  getFirstAvailableAdminRoute,
  getMainSectionItemsForProduct,
  isAdminCenterShellPath,
} from "./sideMenuVisibility";
import { isProductionEnvironment } from "./sideMenuEnv";
import { RAIL_WIDTH_COLLAPSED, RAIL_WIDTH_EXPANDED } from "./sideMenuDensity";
import { isEmailInAdminCenterWhitelist } from "./sideMenuAdminWhitelist";
import { useSideMenuBrandLogos } from "./hooks/useSideMenuBrandLogos";
import { useSideMenuProductSelection } from "./hooks/useSideMenuProductSelection";
import { useKontrolNetworkAlertsForSideMenu } from "./hooks/useKontrolNetworkAlertsForSideMenu";
import { useSideMenuMobile } from "./hooks/useSideMenuMobile";
import { ChangelogNewItemsProvider } from "@shared/changelog/ChangelogNewItemsContext";
import SideMenuBody from "./components/SideMenuBody";
import SideMenuMobileShell from "./components/SideMenuMobileShell";
import type { AdminCenterPanelGroup } from "./components/AdminCenterPanel";
import { useBlockingWindow } from "@shared/hooks/useMaintenanceStatus";

const SideMenu: React.FC = () => {
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { isMobile, isMobileMenuOpen, closeMobileMenu } = useSideMenuMobile();

  const { isLoading, isAuthenticated, getAccessTokenSilently, user } = useAuth0();

  const userProfile = useAppSelector((state: RootState) => state.userProfile);
  const isProfileLoading = useAppSelector(selectIsUserProfileLoading);
  const isExpanded = useAppSelector((state: RootState) => state.sideMenu.isExpanded);
  const {
    TenantType: tenantType,
    canAccessAlerts,
    ListPermiso: userPermissions,
  } = userProfile;

  const { tenantId, partnerId, tenantName, partnerName } = useAppSelector(
    (state: RootState) => state.selection
  );

  const canShowKontrolAlertsMenu = useMemo(
    () =>
      canAccessKontrolAlerts(tenantType, {
        tenantId,
        partnerId,
        tenantName,
        partnerName,
      }),
    [tenantType, tenantId, partnerId, tenantName, partnerName]
  );

  const showAdminCenterByEmail = useMemo(
    () => isEmailInAdminCenterWhitelist(user?.email),
    [user?.email]
  );

  // Who can open the Admin center — same rule as the former Admin Center footer
  // link (GLOBAL_ADMIN or email whitelist).
  const canAccessAdminCenter =
    tenantType === "GLOBAL_ADMIN" || showAdminCenterByEmail;

  const { logoSrc, handleImageError } = useSideMenuBrandLogos();

  const {
    productOptions,
    selectedProduct,
    onSelectProduct,
    filteredServiceItems,
    internalName,
  } = useSideMenuProductSelection(userProfile.ProductList, dispatch);

  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: boolean;
  }>({});
  const [activeItem, setActiveItem] = useState("");

  const handleToggle = () => {
    dispatch(toggleMenu());
  };

  /* A blocking window replaces the Alerts page, so a count beside it points
     at a door that does not open. Hidden rather than frozen, and the poll
     stops with it — the number is neither shown nor actionable. */
  const blockingWindow = useBlockingWindow();

  const { alertsActiveCount, showKontrolAlertsMenuBadge } =
    useKontrolNetworkAlertsForSideMenu({
      internalName,
      tenantId,
      pathname,
      dispatch,
      enabled: !blockingWindow,
    });

  const computedMainItems = useMemo(
    () =>
      getMainSectionItemsForProduct(internalName, mainItems, [
        { label: "Overview", path: "/kontrol", Icon: HomeSvg },
      ]),
    [internalName]
  );

  const filteredEventManagementItems = useMemo(() => {
    if (internalName !== ProductInternalName.KontrolEdge) return [];
    return filterKontrolEventManagementItems(
      eventManagementItems,
      tenantType,
      canShowKontrolAlertsMenu
    );
  }, [internalName, tenantType, canShowKontrolAlertsMenu]);

  const eventManagementItemsForMenu = useMemo((): MenuItem[] => {
    return filteredEventManagementItems.map((item) => {
      if (
        item.id !== "kontrol-alerts" ||
        !showKontrolAlertsMenuBadge ||
        blockingWindow
      ) {
        return item;
      }
      const n = alertsActiveCount ?? 0;
      const badge = n > 99 ? "99+" : String(n);
      return { ...item, badge };
    });
  }, [
    filteredEventManagementItems,
    showKontrolAlertsMenuBadge,
    alertsActiveCount,
    blockingWindow,
  ]);

  const reflectorV2MenuSections = useMemo(() => {
    if (internalName !== ProductInternalName.CtxReflectorV2) {
      return [] as MenuItem[];
    }
    return reflectorV2MenuItems;
  }, [internalName]);

  /**
   * The rail's closing "Account" section (CTX-512), identical for every product.
   *
   * Gating stays `filterAccountAreaItems`, the same set of predicates that
   * filtered these rows inside the drill-in — the rows moved, the rules did not.
   */
  const accountItemsForMenu = useMemo(
    () =>
      filterAccountAreaItems(
        accountAreaItems,
        tenantType,
        isProductionEnvironment()
      ),
    [tenantType]
  );

  useEffect(() => {
    const allItems: MenuItem[] = [
      ...computedMainItems,
      ...filteredServiceItems,
      ...eventManagementItemsForMenu,
      ...managementItems,
      ...insightMenuItems,
      // Reflector V2 sections must be walked too so its Overview/items light up
      // when the route is /reflector-v2/* (these are NOT part of `mainItems` etc.)
      ...reflectorV2MenuSections,
      // Account rows render in every product, so an `/account/*` route has to
      // light one up here too — without this the rail shows no active row on
      // the very screens the section exists to reach.
      ...accountItemsForMenu,
    ];
    expandMatchingParents(
      allItems,
      location.pathname,
      setExpandedSections,
      setActiveItem
    );
  }, [
    location.pathname,
    filteredServiceItems,
    computedMainItems,
    eventManagementItemsForMenu,
    reflectorV2MenuSections,
    accountItemsForMenu,
  ]);

  const filteredAdminSections = useMemo(() => {
    const byPermission = filterAdminSectionsByPermission(
      adminSections,
      userPermissions || []
    );
    // Subscriptions carries no `requiredPermission`, so it survives the
    // permission filter by design and is gated on tenant type instead — see
    // `filterSubscriptionsAdminSection` for why it can't reuse the section
    // convention.
    return filterSubscriptionsAdminSection(
      byPermission,
      tenantType,
      isProductionEnvironment()
    );
  }, [userPermissions, tenantType]);

  // Which product's Admin Center to show. Keyed on the active product
  // (`internalName`), NOT the pathname: the Admin Center now lives inside the
  // Settings drill-in at /account/*, where the pathname carries no product.
  // `internalName` persists the last product on /account routes, so each
  // product keeps showing its own Admin Center.
  const activeAdminSections = useMemo(() => {
    if (internalName === ProductInternalName.CtxReflectorV2) {
      return reflectorV2AdminSections;
    }
    if (internalName === ProductInternalName.CtxReflector) {
      return reflectorV1AdminSections;
    }
    return filteredAdminSections;
  }, [internalName, filteredAdminSections]);

  /** The panel's single group: the active product's Admin center. */
  const adminCenterGroups = useMemo<AdminCenterPanelGroup[]>(() => {
    if (!canAccessAdminCenter || !activeAdminSections.length) return [];
    return [
      {
        id: "admin-center",
        title: "Admin center",
        adminSections: activeAdminSections,
      },
    ];
  }, [canAccessAdminCenter, activeAdminSections]);

  // Legacy v1 at /kontrol/notifications-management is gated by canAccessAlerts.
  useEffect(() => {
    if (pathname === "/kontrol/notifications-management" && canAccessAlerts === false) {
      navigate("/", { replace: true });
    }
  }, [canAccessAlerts, navigate, pathname]);

  /**
   * Panel visibility. Admin-center routes only: `/account/*` used to open it
   * too, back when those screens' navigation lived inside it, and keeping that
   * would hide the product nav on the very rows that moved out to the rail
   * (CTX-512, CTX-581).
   */
  const [showAdminCenter, setShowAdminCenter] = useState(false);

  useEffect(() => {
    setShowAdminCenter(isAdminCenterShellPath(location.pathname));
  }, [location.pathname]);

  /**
   * Where the account card's Admin center row lands. The sections are already
   * permission-filtered, so their first route is a destination this user can
   * actually open — and `null` when there is none to open.
   */
  const firstAdminRoute = useMemo(
    () => getFirstAvailableAdminRoute(activeAdminSections),
    [activeAdminSections]
  );

  const handleOpenAdminCenter = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setShowAdminCenter(true);
      if (isAdminCenterShellPath(location.pathname)) return;
      if (firstAdminRoute) {
        navigate(firstAdminRoute);
      }
    },
    [firstAdminRoute, location.pathname, navigate]
  );

  const handleAdminCenterBack = useCallback(() => {
    setShowAdminCenter(false);
    handleSelection(internalName, navigate);
  }, [internalName, navigate]);

  const handleOpenDocumentation = useCallback(() => {
    const base =
      process.env.REACT_APP_GITBOOK_URL || "https://docs.transitxchange.cloud";
    const url = userProfile.gitbookToken
      ? `${base}?jwt_token=${userProfile.gitbookToken}`
      : base;
    window.open(url, "_blank", "noopener,noreferrer");
  }, [userProfile.gitbookToken]);

  const handleOpenHelp = useCallback(() => {
    navigate("/support/request");
  }, [navigate]);

  const menuBody = (isMobileView: boolean) => (
    <SideMenuBody
      showAdminCenter={showAdminCenter}
      adminCenter={{
        onBack: handleAdminCenterBack,
        onNavigate: (path: string) => {
          if (path) navigate(path);
        },
        activePath: location.pathname,
        groups: adminCenterGroups,
        logoSrc,
        onLogoError: handleImageError,
        showDocumentation: true,
        onOpenDocumentation: handleOpenDocumentation,
        onOpenHelp: handleOpenHelp,
      }}
      standard={{
        isMobileView,
        isExpanded,
        logoSrc,
        onLogoError: handleImageError,
        onToggleExpand: handleToggle,
        onCloseMobile: closeMobileMenu,
        productOptions,
        selectedProduct,
        onSelectProduct,
        tenantType: tenantType ?? "",
        pathname,
        auth0: { isLoading, isAuthenticated, getAccessTokenSilently },
        internalName,
        expandedSections,
        setExpandedSections,
        activeItem,
        setActiveItem,
        navigate,
        computedMainItems,
        filteredServiceItems,
        eventManagementItemsForMenu,
        reflectorV2MenuSections,
        accountItems: accountItemsForMenu,
        gitbookToken: userProfile.gitbookToken,
        /* The gate is `canAccessAdminCenter`, stated rather than inferred from
           whether the panel came out empty. The second half covers the one case
           the first does not: a whitelisted email with no admin permissions, who
           passes the gate and still has no section to open. */
        showAdminCenterRow: canAccessAdminCenter && firstAdminRoute !== null,
        onOpenAdminCenter: handleOpenAdminCenter,
        onOpenHelp: handleOpenHelp,
        isProfileLoading,
      }}
    />
  );

  return (
    /* Wraps both shells rather than sitting inside `menuBody`: the mobile and
       desktop trees are alternative renders of the same rail, and a provider per
       shell would run the derivation twice on a viewport that mounts both. */
    <ChangelogNewItemsProvider pathname={pathname} tenantType={tenantType}>
      {isMobile && (
        <SideMenuMobileShell
          isOpen={isMobileMenuOpen}
          onBackdropClick={closeMobileMenu}
        >
          {menuBody(true)}
        </SideMenuMobileShell>
      )}

      {!isMobile && (
        <aside
          className={`
            relative
            ${isExpanded || showAdminCenter ? RAIL_WIDTH_EXPANDED : RAIL_WIDTH_COLLAPSED}
            transition-all duration-300
            h-screen
            flex flex-col
            bg-semantic-bg-primary
            text-gray-700
            border border-semantic-border-primary
          `}
        >
          {menuBody(false)}
        </aside>
      )}
    </ChangelogNewItemsProvider>
  );
};

export default SideMenu;
